import { Link } from 'react-router-dom';
import { formatPln } from '../catalog/money';
import { markStyle, useContractorColors } from '../contractors/colors';
import { splitSummary, type SplitRow } from './split';
import { supplierAddUrl } from './supplierAdd';
import { MANUAL_STATUSES, RENTAL_STATUSES, type RentalStatus } from './types';
import { AvailabilityEditor } from './AvailabilityEditor';
import { manualLines } from './room';

/** What one supplier's rental does differently from the shared fields („inne dla tego dostawcy”). */
export type SplitOverride = { open: boolean; firstDay?: string | undefined; lastDay?: string | undefined; status?: RentalStatus | undefined; returnPersonId?: string | undefined; notes?: string | undefined };

type Props = {
  itemId: string;
  rows: readonly SplitRow[];
  need: number;
  days: number | null;
  quantities: ReadonlyMap<string, number>;
  onQuantity: (priceId: string, quantity: number) => void;
  overrides: Readonly<Record<string, SplitOverride>>;
  onOverride: (priceId: string, o: SplitOverride) => void;
  /** „Połącz z moim” of a similar-name row (only for whoever may change the suppliers' catalogs). */
  mayLink: boolean;
  links: ReadonlySet<string>;
  onLink: (priceId: string, on: boolean) => void;
  /** Which shared fields the role may write (rule 7a). */
  sees: (field: string) => boolean;
  persons: ReadonlyArray<{ id: string; name: string }> | undefined;
  /** Suppliers without the item — „Dodaj w katalogu dostawcy” when all together have too few. */
  lacking: ReadonlyArray<{ id: string; name: string }>;
  back: string;
  /** „zmień dostępność” for the rental's days (whoever may add rentals and sees the suppliers' quantities). */
  availability?: { firstDay: string; lastDay: string } | null;
};

const stockText = (r: SplitRow): string | null => {
  if (r.stock === undefined) return null; // rule 7a: not seen
  if (r.stock === null) return 'ilość niepotwierdzona';
  return `ma ${r.stock} szt.${r.taken ? ` (już ${r.taken} dopożyczone w tych dniach)` : ''}`;
};

/**
 * Owner 2026-10-24 10:40: „Dopożycz” from several suppliers at once — a row per supplier having the item (its name in its
 * colour, what it has, its price per day and a quantity, prefilled by `defaultSplit`), each capped at what the supplier
 * still has for these days („max N”; the server checks it too). Below: „Razem: X z N · brakuje jeszcze M” and the cost.
 * A row's „inne dla tego dostawcy” overrides the shared days, status, person or notes for that supplier.
 */
export function RentalSplit({ itemId, rows, need, days, quantities, onQuantity, overrides, onOverride, mayLink, links, onLink, sees, persons, lacking, back, availability }: Props) {
  const colors = useContractorColors();
  const summary = splitSummary(rows, quantities, need, days);
  // shown in the order of the split: the most still free first, then unknown stock, similar names last
  const rank = (r: SplitRow) => (r.match === 'similar' ? 2 : typeof r.available === 'number' ? 0 : 1);
  const shown = [...rows].sort((a, b) => rank(a) - rank(b) || (typeof a.available === 'number' && typeof b.available === 'number' ? b.available - a.available : 0));
  return (
    <div className="rental-split" data-feature-id="subrentals.tab">
      <p className="muted small">Od kogo i ile — domyślnie najpierw dostawca, który ma najwięcej; resztę biorą kolejni.</p>
      <ul className="rental-split-list" aria-label="Dostawcy, którzy mają ten sprzęt">
        {shown.map((r) => {
          const q = quantities.get(r.priceId) ?? 0;
          const cap = typeof r.available === 'number' ? r.available : undefined;
          const o = overrides[r.priceId] ?? { open: false };
          const stock = stockText(r);
          return (
            <li key={r.priceId} className="rental-split-row" data-price-id={r.priceId}>
              <div className="rental-split-main">
                <span className="rental-split-name" style={markStyle(colors.get(r.supplierId))}>
                  {r.supplierName}
                </span>
                <span className="muted small rental-split-facts">
                  {[r.match === 'similar' ? 'podobna nazwa' : null, stock, r.priceDayGrosze !== undefined ? `${formatPln(r.priceDayGrosze)}/doba` : null].filter(Boolean).join(' · ')}
                </span>
                <label className="rental-split-qty">
                  <input
                    inputMode="numeric"
                    aria-label={`Ilość od ${r.supplierName}`}
                    value={q === 0 ? '' : String(q)}
                    placeholder="0"
                    max={cap}
                    onChange={(e) => {
                      const n = Number(e.target.value.replace(/\D/g, '') || '0');
                      // owner 2026-10-24 16:15: more than the supplier still has — the max
                      onQuantity(r.priceId, Number.isSafeInteger(n) ? (cap !== undefined ? Math.min(n, cap) : n) : 0);
                    }}
                  />
                  {cap !== undefined && (
                    <span className={`muted small rental-max${q > cap ? ' over' : ''}`} title={['Zostało u dostawcy w tych dniach (wszystkie wydarzenia)', ...manualLines(r.manual)].join('\n')}>
                      max {cap}
                    </span>
                  )}
                </label>
              </div>
              {availability && r.stock !== undefined && r.match === 'exact' && (
                <AvailabilityEditor key={`${r.priceId}:${availability.firstDay}:${availability.lastDay}`} positionId={r.priceId} firstDay={availability.firstDay} lastDay={availability.lastDay} manual={r.manual ?? []} label={`Dostępność u ${r.supplierName} w tych dniach`} />
              )}
              {r.match === 'similar' && q > 0 && mayLink && r.linkable !== false && (
                <label className="check small">
                  <input type="checkbox" checked={links.has(r.priceId)} onChange={(e) => onLink(r.priceId, e.target.checked)} />
                  <span>Połącz tę pozycję {r.supplierName} z moim sprzętem (następnym razem znajdzie się od razu)</span>
                </label>
              )}
              {q > 0 && (
                <button type="button" className="btn small ghost rental-split-other" aria-expanded={o.open} onClick={() => onOverride(r.priceId, { ...o, open: !o.open })}>
                  inne dla tego dostawcy
                </button>
              )}
              {q > 0 && o.open && (
                <div className="rental-split-override field-row">
                  {sees('firstDay') && (
                    <label className="field">
                      <span>Odbiór</span>
                      <input type="date" value={o.firstDay ?? ''} onChange={(e) => onOverride(r.priceId, { ...o, firstDay: e.target.value || undefined })} />
                    </label>
                  )}
                  {sees('lastDay') && (
                    <label className="field">
                      <span>Zwrot</span>
                      <input type="date" value={o.lastDay ?? ''} onChange={(e) => onOverride(r.priceId, { ...o, lastDay: e.target.value || undefined })} />
                    </label>
                  )}
                  {sees('status') && (
                    <label className="field">
                      <span>Status</span>
                      <select value={o.status ?? ''} onChange={(e) => onOverride(r.priceId, { ...o, status: (e.target.value || undefined) as RentalStatus | undefined })}>
                        <option value="">jak wszystkie</option>
                        {MANUAL_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {RENTAL_STATUSES[s]}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {sees('returnPersonId') && persons && (
                    <label className="field">
                      <span>Kto oddaje</span>
                      <select value={o.returnPersonId ?? ''} onChange={(e) => onOverride(r.priceId, { ...o, returnPersonId: e.target.value || undefined })}>
                        <option value="">jak wszystkie</option>
                        {persons.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {sees('notes') && (
                    <label className="field">
                      <span>Uwagi</span>
                      <input value={o.notes ?? ''} maxLength={2000} onChange={(e) => onOverride(r.priceId, { ...o, notes: e.target.value || undefined })} />
                    </label>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <p className={`rental-split-summary${summary.missing > 0 ? ' short' : ''}`} data-testid="split-summary">
        Razem: {summary.total} z {need}
        {summary.missing > 0 && ` · brakuje jeszcze ${summary.missing}`}
        {summary.costGrosze !== null && summary.total > 0 && ` · koszt ${formatPln(summary.costGrosze)}`}
      </p>
      {summary.missing > 0 && lacking.length > 0 && (
        <p className="muted small rental-split-lacking">
          Dodaj tę pozycję w katalogu dostawcy:{' '}
          {lacking.slice(0, 4).map((s, i) => (
            <span key={s.id}>
              {i > 0 && ', '}
              <Link to={supplierAddUrl(s.id, itemId, back)}>{s.name}</Link>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
